/**
 * Confirmation dialog for dismissing an open incident — structurally follows
 * ConfirmModal's conventions (title, body, loading/error, Cancel/Confirm),
 * but with a required free-text reason instead of a type-to-confirm phrase.
 * No existing pro-web component has a reason-text-field pattern to reuse
 * (ConfirmModal's `confirmPhrase` is verification-by-typing, not free text).
 */
import * as React from "react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import * as s from "./DismissAlertModal.css";

export interface DismissAlertModalProps {
  open: boolean;
  alertName: string;
  alertCode: string;
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export function DismissAlertModal({
  open,
  alertName,
  alertCode,
  loading = false,
  error = null,
  onCancel,
  onConfirm,
}: DismissAlertModalProps) {
  const [reason, setReason] = React.useState("");

  React.useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const handleClose = React.useCallback(() => {
    if (loading) return;
    onCancel();
  }, [loading, onCancel]);

  const trimmedReason = reason.trim();

  return (
    <Modal
      open={open}
      setOpen={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
      minWidth="420px"
      minHeight="auto"
      maxWidth="480px"
    >
      <Modal.Header>
        <h3>Dismiss Alert: {alertName} ({alertCode})</h3>
      </Modal.Header>
      <Modal.Body>
        <div className={s.body}>
          <p className={s.text}>
            This marks the alert as dismissed and no longer ongoing. This can't be undone.
          </p>
          <div>
            <label className={s.label} htmlFor="dismiss-alert-reason">Reason</label>
            <textarea
              id="dismiss-alert-reason"
              className={s.textarea}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              placeholder="Why is this alert being dismissed?"
              autoFocus
            />
          </div>
          {error && <Callout variant="error">{error}</Callout>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button color="neutral" variant="outline" size="md" disabled={loading} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          color="danger"
          variant="solid"
          size="md"
          loading={loading}
          disabled={!trimmedReason}
          onClick={() => onConfirm(trimmedReason)}
        >
          Dismiss
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
